/**
 * Template save handler with screenshot capture
 */
import * as htmlToImage from 'html-to-image'
import config from '@/lib/config'
import type { ContainerElement, AbsoluteElement } from '../types/template.types'

export interface SaveHandlerParams {
  templateId: string | undefined
  isNewTemplate: boolean
  templateName: string
  globalFontFamily: string
  globalFontSize: number
  globalFontColor: string
  headerContainer: ContainerElement
  footerContainer: ContainerElement
  rootContainer: ContainerElement | ContainerElement[] | null
  absoluteElements: AbsoluteElement[]
  canvasRef: React.RefObject<HTMLDivElement>
  setHasUnsavedChanges: (value: boolean) => void
  leftMargin: number
  rightMargin: number
  topMargin: number
  bottomMargin: number
}

export interface SaveHandlerCallbacks {
  onSuccess: () => void
  onError: (error: Error) => void
  onValidationError: (message: string) => void
}

/**
 * Remove cellAlignments from container and all nested containers recursively
 */
function removeCellAlignments(container: ContainerElement): ContainerElement {
  const { cellAlignments, ...containerWithoutCellAlignments } = container;
  
  // Recursively process children
  if (container.children && container.children.length > 0) {
    containerWithoutCellAlignments.children = container.children.map(child => {
      if (child.element.type === 'container') {
        return {
          ...child,
          element: removeCellAlignments(child.element as ContainerElement),
        };
      }
      return child;
    });
  }
  
  return containerWithoutCellAlignments;
}

export const createSaveHandler = (
  params: SaveHandlerParams,
  callbacks: SaveHandlerCallbacks
) => {
  return async () => {
    const {
      templateId,
      isNewTemplate,
      templateName,
      globalFontFamily,
      globalFontSize,
      globalFontColor,
      headerContainer,
      footerContainer,
      rootContainer,
      absoluteElements,
      canvasRef,
      setHasUnsavedChanges,
    } = params

    const { onSuccess, onError, onValidationError } = callbacks

    try {
      // Validate template name
      if (!templateName.trim()) {
        onValidationError('Template name is required')
        return
      }

      // Capture screenshot using html-to-image
      let screenshot = ''
      if (canvasRef.current) {
        try {
          console.log('Starting screenshot capture with html-to-image...')
          
          // Use toPng for better quality and compatibility
          screenshot = await htmlToImage.toPng(canvasRef.current, {
            backgroundColor: '#ffffff',
            pixelRatio: 0.5, // Reduce size for storage
            filter: (node) => {
              // Filter out SVG elements (grid, rulers)
              if (node.tagName === 'svg') {
                return false
              }
              // Filter out elements with ignore-screenshot class
              if (node.classList?.contains('ignore-screenshot')) {
                return false
              }
              return true
            },
          })
          
          console.log('Screenshot captured successfully, size:', screenshot.length)
        } catch (screenshotError) {
          console.error('Screenshot capture failed:', screenshotError)
          // Continue without screenshot
          screenshot = ''
        }
      } else {
        console.warn('Canvas ref is not available for screenshot')
      }

      const templateData = {
        name: templateName,
        fontFamily: globalFontFamily,
        fontSize: globalFontSize,
        fontColor: globalFontColor,
        rootContainer: {
          header: headerContainer ? removeCellAlignments(headerContainer) : undefined,
          footer: footerContainer ? removeCellAlignments(footerContainer) : undefined,
          containers: rootContainer 
            ? (Array.isArray(rootContainer) 
                ? rootContainer.map(c => removeCellAlignments(c))
                : removeCellAlignments(rootContainer))
            : undefined,
          absoluteElements: absoluteElements.length > 0 ? absoluteElements : undefined,
          leftMargin: params.leftMargin,
          rightMargin: params.rightMargin,
          topMargin: params.topMargin,
          bottomMargin: params.bottomMargin,
        },
        screenshot,
      }

      const token = localStorage.getItem('auth-data')

      if (isNewTemplate) {
        // Create new template
        const response = await fetch(`${config.apiUrl}/api/invoice-template`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify(templateData),
        })

        if (!response.ok) {
          throw new Error('Failed to create template')
        }
      } else {
        // Update existing template by ID
        const response = await fetch(`${config.apiUrl}/api/invoice-template/${templateId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify(templateData),
        })

        if (!response.ok) {
          throw new Error('Failed to update template')
        }
      }

      // Reset unsaved changes flag after successful save
      setHasUnsavedChanges(false)

      onSuccess()
    } catch (error) {
      console.error('Save error:', error)
      onError(error instanceof Error ? error : new Error('Unknown error'))
    }
  }
}
